import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bookmark } from '../site/Bookmark';
import './install-prompt.css';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const LATER_KEY = 'pwa-install-later-until';
const LATER_DAYS = 7;

// A small chalk card offering to install the site as an app. "Later" hides it for a week.
export const InstallPrompt: React.FC = () => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);

  useEffect(() => {
    let laterUntil = 0;
    try {
      laterUntil = Number(localStorage.getItem(LATER_KEY)) || 0;
      localStorage.removeItem('pwa-install-deferred'); // the old flag never expired
    } catch {
      // storage blocked: just offer the prompt
    }
    if (Date.now() < laterUntil) return;
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    let timer: number | undefined;
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      timer = window.setTimeout(() => setShowPrompt(true), 3000);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch (error) {
      console.error('Error showing install prompt:', error);
    }
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    try {
      localStorage.setItem(LATER_KEY, String(Date.now() + LATER_DAYS * 24 * 60 * 60 * 1000));
    } catch {
      // storage blocked: it comes back next visit
    }
  };

  if (!showPrompt || !deferredPrompt) return null;

  return (
    <div className="install-tip" role="dialog" aria-labelledby="install-tip-title" aria-describedby="install-tip-text">
      <Bookmark size={34} className="install-tip-mark" />
      <div className="install-tip-body">
        <h2 id="install-tip-title">{t('pwa.install.title', 'Install GakuShiori')}</h2>
        <p id="install-tip-text">{t('pwa.install.description', 'Open your class board in one click, even without Wi-Fi.')}</p>
        <div className="install-tip-actions">
          <button type="button" className="install-tip-go" onClick={handleInstall}>
            {t('pwa.install.install', 'Install')}
          </button>
          <button type="button" className="install-tip-later" onClick={handleDismiss}>
            {t('pwa.install.later', 'Later')}
          </button>
        </div>
      </div>
    </div>
  );
};
